import type { Order } from '__models__'
import get from 'lodash-es/get'
import React from 'react'

import { TableCell } from '@components/common/TableCell'
import { TableProductsList } from '@components/common/TableProductsList'
import { TableItemDate } from '@components/utils/TableItemDate'
import type { TableHeaders } from '@fourthwall/components'
import { Icon, PriceString, ProfitString, Tag, Tooltip } from '@fourthwall/components'
import { QuestionMarkIcon } from '@fourthwall/icons'
import { getNumberOfProducts } from '@utils/products'

import { TableStatusTag } from '../modules/TableStatusTag'
import { getBackerName } from './getBackerName'

export const getOrdersTableHeaders = ({
  hideProfitColumn,
  timezoneAbbreviation,
}: {
  hideProfitColumn: boolean
  timezoneAbbreviation: string
}): TableHeaders<Order> => {
  const headers: TableHeaders<Order> = [
    {
      id: 'createdAt',
      label: `Date (${timezoneAbbreviation})`,
      render: ({ createdAt }) => (
        <div>
          <TableItemDate date={createdAt} />
        </div>
      ),
    },
    {
      id: 'products',
      label: ' ',
      render: ({ offers, donation, offersCancelled }) => (
        <TableProductsList
          offers={offers}
          offersCancelled={offersCancelled}
          donation={donation}
          quantity={getNumberOfProducts(offers, donation)}
        />
      ),
    },
    {
      id: 'shipping.address.firstName',
      label: 'Supporter',
      render: (order) => <TableCell mainContent={getBackerName(order)} $ellipsis />,
      width: 0,
      minWidth: 220,
    },
    {
      id: 'status',
      label: 'Status',
      render: ({ id, status, salesChannel }) => (
        <div>
          <TableStatusTag orderId={id} status={status} />
          {salesChannel.type === 'Meta' && (
            <div style={{ marginTop: 8 }}>
              <Tag label="From Meta" />
            </div>
          )}
          {salesChannel.type === 'TikTok' && (
            <div style={{ marginTop: 8 }}>
              <Tag label="From TikTok" />
            </div>
          )}
        </div>
      ),
      width: 2,
    },
    {
      id: 'totalPrice.value',
      label: 'Total',
      render: ({ totalPrice }) => (
        <TableCell mainContent={<PriceString value={get(totalPrice, 'value')} />} />
      ),
    },
    {
      id: 'profit',
      hidden: hideProfitColumn,
      label: (
        <Tooltip
          reference={
            <span
              style={{
                display: 'grid',
                gridAutoFlow: 'column',
                alignItems: 'center',
                gap: 3,
              }}
            >
              Profit <Icon component={QuestionMarkIcon} height={12} />
            </span>
          }
        >
          <span style={{ textTransform: 'none' }}>
            If the order contains MOQ screen printed products, profit values are estimated. The
            final profit values will be available within earnings history.
          </span>
        </Tooltip>
      ),
      render: ({ profit }) => (
        <TableCell mainContent={profit ? <ProfitString value={profit.value} /> : <>&ndash;</>} />
      ),
    },
  ]

  return headers.filter((column) => !column.hidden)
}
